import React, { useEffect, useState } from "react";
import { Loader2, Plus, Check, EyeOff, Eye, Trash2, RotateCcw } from "lucide-react";
import api from "../lib/api";
import { useTips } from "./CoachTip";

const SCREEN_LABEL = {
  feed: "Feed", compose: "Transmit (compose)", "profile-own": "Your profile",
  "profile-other": "Member profile", meetup: "Encrypted Meet Up", stream: "Conscious Stream", beacon: "Beacon editor (creators)", "beacon-guide": "Beacon Guide topics (in order)",
};

const TipRow = ({ tip, onSaved }) => {
  const [title, setTitle] = useState(tip.title);
  const [body, setBody] = useState(tip.body);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const dirty = title !== tip.title || body !== tip.body;

  const save = async () => {
    setBusy(true);
    try { await api.put(`/admin/tips/${tip.tip_id}`, { title, body }); setSaved(true); await onSaved(); setTimeout(() => setSaved(false), 2000); }
    finally { setBusy(false); }
  };
  const toggleActive = async () => { setBusy(true); try { await api.put(`/admin/tips/${tip.tip_id}`, { active: !tip.active }); await onSaved(); } finally { setBusy(false); } };
  const isAnn = !tip.builtin && tip.screen === "feed";
  const locked = tip.can_manage === false;
  const remove = async () => { if (!window.confirm(isAnn ? "Remove this announcement for everyone?" : "Remove this tip?")) return; setBusy(true); try { await api.delete(`/admin/tips/${tip.tip_id}`); await onSaved(); } finally { setBusy(false); } };

  return (
    <div className={`border border-white/10 p-4 ${tip.active ? "" : "opacity-50"}`} data-testid={`tip-row-${tip.tip_id}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: tip.color }} />
          <span className="font-body text-[10px] tracking-wider uppercase text-zinc-500 truncate">{tip.tip_id} · rev {tip.rev}{tip.builtin ? "" : " · custom"}</span>
        </div>
        {locked ? <span className="font-body text-[10px] text-zinc-600 shrink-0" data-testid={`tip-locked-${tip.tip_id}`}>Posted by another Council member</span> : <div className="flex items-center gap-3 shrink-0">
          <button onClick={toggleActive} disabled={busy} title={tip.active ? "Hide from members" : "Show to members"} className="text-zinc-500 hover:text-white transition-colors" data-testid={`tip-toggle-${tip.tip_id}`}>
            {tip.active ? <Eye size={15} /> : <EyeOff size={15} />}
          </button>
          <button onClick={remove} disabled={busy} title={tip.builtin ? "Hide built-in tip" : "Delete tip"} className="text-zinc-600 hover:text-[#E5484D] transition-colors" data-testid={`tip-delete-${tip.tip_id}`}><Trash2 size={15} /></button>
        </div>}
      </div>
      <input value={title} disabled={locked} onChange={(e) => setTitle(e.target.value)} placeholder="Title" data-testid={`tip-title-${tip.tip_id}`} className="w-full bg-transparent border border-white/15 px-3 py-2 mb-2 text-white text-sm font-heading focus:outline-none focus:border-[#14A89A]" />
      <textarea value={body} disabled={locked} onChange={(e) => setBody(e.target.value)} rows={3} placeholder="Body: wrap **words** in asterisks to emphasise" data-testid={`tip-body-${tip.tip_id}`} className="w-full bg-transparent border border-white/15 px-3 py-2 text-zinc-300 text-xs font-body resize-none focus:outline-none focus:border-[#14A89A]" />
      <div className="flex items-center justify-between mt-2">
        <span className="font-body text-[10px] text-zinc-600">Saving re-shows only this tip to everyone.</span>
        <button onClick={save} disabled={!dirty || busy} data-testid={`tip-save-${tip.tip_id}`} className="flex items-center gap-1.5 bg-[#14A89A] text-[#0A0A0A] px-3 py-1.5 font-body text-xs disabled:opacity-40 hover:bg-[#2BC4B5] transition-colors">
          {busy ? <Loader2 size={13} className="animate-spin" /> : saved ? <Check size={13} /> : null}{saved ? "Saved" : "Save"}
        </button>
      </div>
    </div>
  );
};

export const TipsManager = () => {
  const { reloadTips } = useTips();
  const [tips, setTips] = useState(null);
  const [nt, setNt] = useState({ title: "", body: "" });
  const [creating, setCreating] = useState(false);
  const [replaying, setReplaying] = useState(false);

  const load = async () => { const { data } = await api.get("/admin/tips"); setTips(data); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const refresh = async () => { await load(); reloadTips(); };

  const createTip = async () => {
    if (!nt.title.trim() || !nt.body.trim()) return;
    setCreating(true);
    try { await api.post("/admin/tips", { title: nt.title, body: nt.body, screen: "feed", priority: 5, icon: "Megaphone", color: "#D4AF37" }); setNt({ title: "", body: "" }); await refresh(); }
    finally { setCreating(false); }
  };
  const addGuideTopic = async () => {
    await api.post("/admin/tips", { title: "New guide topic", body: "Explain the feature here, wrap **words** in asterisks to emphasise.", screen: "beacon-guide", priority: 100, icon: "Info", color: "#14A89A" });
    await refresh();
  };
  const replayMine = async () => { setReplaying(true); try { await api.post("/tips/reset"); reloadTips(); setTimeout(() => setReplaying(false), 1500); } catch { setReplaying(false); } };

  if (!tips) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-zinc-500" /></div>;

  const groups = {};
  const posted = tips.filter((t) => !t.builtin && t.screen === "feed").sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  tips.filter((t) => !posted.includes(t)).forEach((t) => { (groups[t.screen] = groups[t.screen] || []).push(t); });

  return (
    <div data-testid="tips-tab" className="space-y-8">
      <p className="font-body text-sm text-zinc-400 leading-relaxed">Edit the guided tips members see. Each tip shows once; editing a tip's wording re-shows <span className="text-white">only that tip</span> to everyone, the others stay dismissed. New announcements appear at the top of the Feed.</p>

      <div className="border border-[#D4AF37]/25 bg-[#D4AF37]/[0.05] p-4">
        <div className="font-heading text-sm text-[#D4AF37] mb-3">New announcement (shows on the Feed)</div>
        <input value={nt.title} onChange={(e) => setNt({ ...nt, title: e.target.value })} placeholder="Title: e.g. New: Living Library" data-testid="new-tip-title" className="w-full bg-transparent border border-white/15 px-3 py-2 mb-2 text-white text-sm font-heading focus:outline-none focus:border-[#D4AF37]" />
        <textarea value={nt.body} onChange={(e) => setNt({ ...nt, body: e.target.value })} rows={2} placeholder="Body: wrap **words** in asterisks to emphasise" data-testid="new-tip-body" className="w-full bg-transparent border border-white/15 px-3 py-2 text-zinc-300 text-xs font-body resize-none focus:outline-none focus:border-[#D4AF37]" />
        <div className="flex justify-end mt-2">
          <button onClick={createTip} disabled={creating || !nt.title.trim() || !nt.body.trim()} data-testid="new-tip-create" className="flex items-center gap-1.5 bg-[#D4AF37] text-[#0A0A0A] px-3 py-1.5 font-body text-xs disabled:opacity-40 hover:bg-[#e6c14e] transition-colors">
            {creating ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Publish announcement
          </button>
        </div>
      </div>

      <div data-testid="posted-announcements">
        <div className="font-body text-[11px] tracking-[0.2em] uppercase text-[#D4AF37] mb-3">Posted announcements</div>
        {posted.length ? <div className="space-y-3">{posted.map((t) => <TipRow key={t.tip_id} tip={t} onSaved={refresh} />)}</div>
          : <p className="font-body text-xs text-zinc-600" data-testid="posted-announcements-empty">Nothing posted yet.</p>}
        <p className="font-body text-[10px] text-zinc-600 mt-2">Edit the wording and Save, tap the eye to hide it for now, or the bin to remove it for everyone.</p>
      </div>

      {Object.keys(groups).map((screen) => (
        <div key={screen}>
          <div className="font-body text-[11px] tracking-[0.2em] uppercase text-zinc-500 mb-3 flex items-center justify-between gap-2">{SCREEN_LABEL[screen] || screen}
            {screen === "beacon-guide" && <button onClick={addGuideTopic} data-testid="new-guide-topic" className="flex items-center gap-1 normal-case tracking-normal text-[#14A89A] hover:underline"><Plus size={12} /> Add topic</button>}
          </div>
          <div className="space-y-3">{groups[screen].map((t) => <TipRow key={t.tip_id} tip={t} onSaved={refresh} />)}</div>
        </div>
      ))}

      <button onClick={replayMine} data-testid="replay-my-tips" className="flex items-center gap-2 text-zinc-500 hover:text-[#14A89A] transition-colors font-body text-xs">
        <RotateCcw size={13} /> {replaying ? "Reset: your tips will reappear" : "Replay all tips for my own account"}
      </button>
    </div>
  );
};
